import {
  BufferGeometry, BufferAttribute, Vector3, Mesh,
  Matrix4, Matrix3, Scene
} from "three";
import {Viewer} from '../main';
import {MarkerMaterial} from "../materials";
import {PanoramicImage} from '../panoramics';

export class CameraMarkers {
  private viewer: Viewer;
  private cameras = [];
  public transparency = 0.5;
  private verticesPerCamera = 6;
  private uvs: number[] = [0, 1, 0, 0, 1, 1, 0, 0, 1, 0, 1, 1];
  private corners: number[] = [0, 2, 1, 2, 3, 1];
  private vertices: Vector3[] = [
    new Vector3(-0.5, 0.5, 0),
    new Vector3(0.5, 0.5, 0),
    new Vector3(-0.5, -0.5, 0),
    new Vector3(0.5, -0.5, 0)
  ]
  private standardMesh: any = null;
  private pickingMesh: any = null;
  private standardMaterial: MarkerMaterial = null;
  private pickingMaterial: MarkerMaterial = null;
  public scene: Scene = null;

  constructor(viewer) {
    this.viewer = viewer;
    this.initScene();
    this.initGeometry();
  }

  get controls() {
    return this.viewer.controls;
  }

  get panoramics() {
    return this.viewer.panoramics;
  }

  get picker() {
    return this.viewer.gpuPickers.markers;
  }

  initScene() {
    this.scene = new Scene();
  }

  initGeometry() {
    if (this.standardMesh) {
      this.scene.remove(this.standardMesh);
    }

    if (this.pickingMesh) {
      this.picker.remove(this.pickingMesh);
    }

    let geometry = new BufferGeometry();
    geometry.setAttribute('selected', new BufferAttribute(new Float32Array(0), 1));
    geometry.setAttribute('visible', new BufferAttribute(new Float32Array(0), 1));
    geometry.setAttribute('gpu_index', new BufferAttribute(new Int32Array(0), 1));
    geometry.setAttribute('camera_index', new BufferAttribute(new Int32Array(0), 1));
    geometry.setAttribute('position', new BufferAttribute(new Float32Array(0), 3));
    geometry.setAttribute('uv', new BufferAttribute(new Float32Array(0), 2));

    this.standardMaterial = new MarkerMaterial(this.viewer, false);
    this.standardMesh = new Mesh(geometry, this.standardMaterial);
    this.scene.add(this.standardMesh);

    this.pickingMaterial = new MarkerMaterial(this.viewer, true);
    this.pickingMesh = new Mesh(geometry, this.pickingMaterial);
    this.picker.add(this.pickingMesh);
  }

  addCameraMarkers(cameras: Array<PanoramicImage>) {
    let timeStart = performance.now();

    // Add new cameras to array
    cameras.forEach(camera => {
      const cameraExists = this.cameras.includes(camera);
      if (cameraExists) {
        return;
      }

      this.cameras.push(camera);
    });

    this.drawCameraMarkers();

    if (this.cameras.length > 0) {
      let timeEnd = performance.now();
      let timeElapsed = (timeEnd-timeStart).toFixed(2);
      console.log(`Update ${this.cameras.length} viewer markers: ${timeElapsed}ms`);
    }
  }

  drawCameraMarkers() {
    let numCameras = this.cameras.length;
    let selected = new Float32Array(numCameras * this.verticesPerCamera);
    let visible = new Float32Array(numCameras * this.verticesPerCamera);
    let positions = new Float32Array(numCameras * this.verticesPerCamera * 3);
    let uvs = new Float32Array(numCameras * this.verticesPerCamera * 2);
    let indices = new Int32Array(numCameras * this.verticesPerCamera);
    let cameraIndices = new Int32Array(numCameras * this.verticesPerCamera);

    this.cameras.forEach((camera, camIndex) => {
      let position = camera.position.marker;

      // Setting our positions array
      const numCorners = this.corners.length;
      this.corners.forEach((corner, cornerIndex) => {
        let index = (numCorners * camIndex) + cornerIndex;
        var points = this.vertices[corner];
        positions[(index * 3)] = points.x + position.x;
        positions[(index * 3) + 1] = points.y + position.y;
        positions[(index * 3) + 2] = points.z + position.z;
      });

      // Assign our uv values
      const numUvs = this.uvs.length;
      this.uvs.forEach((uv, uvIndex) => {
        let index = (numUvs * camIndex) + uvIndex;
        uvs[index] = uv;
      });

      // Create attribute arrays for each vertex in each cam.
      const numVertices = this.verticesPerCamera;
      for (var i = 0; i < this.verticesPerCamera; i++) {
        let index = (numVertices * camIndex) + i;
        let cameraIndex = this.cameras.indexOf(camera);
        selected[index] = 0.0;
        visible[index] = 1.0;
        indices[index] = 0.0;
        cameraIndices[index] = cameraIndex;
      }
    });

    let geometry = new BufferGeometry();
    geometry.setAttribute('selected', new BufferAttribute(selected, 1));
    geometry.setAttribute('visible', new BufferAttribute(visible, 1));
    geometry.setAttribute('gpu_index', new BufferAttribute(indices, 1));
    geometry.setAttribute('camera_index', new BufferAttribute(cameraIndices, 1));
    geometry.setAttribute('position', new BufferAttribute(positions, 3));
    geometry.setAttribute('uv', new BufferAttribute(uvs, 2));

    [this.standardMesh, this.pickingMesh].forEach(mesh => {
      mesh.geometry.dispose();
      mesh.geometry.copy(geometry);
      mesh.geometry.needsUpdate = true;
    });

    this.viewer.updatePickers();
  }

  removeCameraMarker(camera: PanoramicImage) {
    // Remove from cameras array
    const index = this.cameras.indexOf(camera);
    this.cameras.splice(index, 1);
  }

  recalculateAllMarkers() {
    const cameras = [...this.cameras];
    cameras.forEach(camera => {
      this.removeCameraMarker(camera);
    });
    this.addCameraMarkers(cameras);
  }

  markCameraSelected(markerID) {
    [this.standardMesh, this.pickingMesh].forEach(mesh => {
      let attributes = mesh.geometry.attributes;
      attributes.selected.array.fill(0.0);
      let index = attributes.camera_index.array.indexOf(markerID);
      if (index !== -1) {
        for (var i = 0; i < this.verticesPerCamera; i++) {
          attributes.selected.array[index + i] = 1.00;
        }
      }

      attributes.selected.needsUpdate = true;
    });

    // Update picker for selected
    this.picker.needsUpdate = true;
  }

  cameraPickerID(event) {
    // Update gpu picker if required
    this.picker.update();

    // Grab our index and marker id
    const index = this.picker.value(event);
    if (index === null) {
      return null;
    }

    const attributes = this.pickingMesh.geometry.attributes;
    const arrIndex = attributes.gpu_index.array.indexOf(index);
    const markerID = attributes.camera_index.array[arrIndex];

    return markerID;
  }

  mouseClick(event) {
    try {
      let markerID = this.cameraPickerID(event);
      if (markerID !== null) {
        let camera = this.cameras[markerID];
        this.panoramics.loadImage(camera.id);
        return true;
      }
    } catch (e) {
      console.log("Prevented framebuffer exception: ");
      console.log(e);
    }

    return false;
  }

  onMouseMove(event, mouse) {
    if (mouse.movement) {
      return;
    }

    try {
      let markerID = this.cameraPickerID(event);
      this.markCameraSelected(markerID);
    } catch (e) {
      console.log("Prevented framebuffer exception: ");
      console.log(e);
    }
  }

  update() {
    let position = this.viewer.camera.position;
    let quat = this.viewer.camera.quaternion;
    let mtrx4 = new Matrix4().makeRotationFromQuaternion(quat);
    let mtrx3 = new Matrix3().setFromMatrix4(mtrx4);
    mtrx3.getInverse(mtrx3);
    let elements = mtrx3.elements;

    [this.standardMaterial, this.pickingMaterial].forEach(shader => {
      let uniforms = shader.uniforms;
      uniforms.mtrx_x.value = new Vector3(elements[0], elements[1], elements[2]);
      uniforms.mtrx_y.value = new Vector3(elements[3], elements[4], elements[5]);
      uniforms.mtrx_z.value = new Vector3(elements[6], elements[7], elements[8]);
      uniforms.camera_position.value = position;
      uniforms.screenHeight.value = this.viewer.height;
      uniforms.currentIndex.value = this.viewer.panoramics.currentIndex;
      uniforms.orbitMode.value = this.viewer.orbitState;
      uniforms.fov.value = this.viewer.camera.fov * Math.PI / 180.0;
      shader.needsUpdate = true;
    });
  }
}
